import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui/Spinner';
import { DashboardPage } from './pages/DashboardPage';
import { ChainsPage } from './pages/ChainsPage';
import { ChainDetailPage } from './pages/ChainDetailPage';
import { NewTradePage } from './pages/NewTradePage';
import { PositionDetailPage } from './pages/PositionDetailPage';
import { RollPositionPage } from './pages/RollPositionPage';
import { AlertsPage } from './pages/AlertsPage';
import { AlertHistoryPage } from './pages/AlertHistoryPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SchwabSettingsPage } from './pages/SchwabSettingsPage';
import { SchwabCallbackPage } from './pages/SchwabCallbackPage';
import { ImportPage } from './pages/ImportPage';
import { ToolsPage } from './pages/ToolsPage';
import { OrphanChainsPage } from './pages/OrphanChainsPage';
import { SymbolChainsPage } from './pages/SymbolChainsPage';
import { OrdersPage } from './pages/OrdersPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { PositionsPage } from './pages/PositionsPage';
import { InsightsPage } from './pages/InsightsPage';
import { TradeIdeasPage } from './pages/TradeIdeasPage';
import { CandidatesPage } from './pages/CandidatesPage';
import { JournalPage } from './pages/JournalPage';
import { LearnPage } from './pages/LearnPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h1>
          <p className="text-gray-600">Please sign in through Cloudflare Access to continue.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="positions" element={<PositionsPage />} />
        <Route path="chains" element={<ChainsPage />} />
        <Route path="chains/new" element={<NewTradePage />} />
        <Route path="chains/link" element={<OrphanChainsPage />} />
        <Route path="chains/:id" element={<ChainDetailPage />} />
        <Route path="symbols/:symbol" element={<SymbolChainsPage />} />
        <Route path="positions/:id" element={<PositionDetailPage />} />
        <Route path="positions/:id/roll" element={<RollPositionPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="alerts/history" element={<AlertHistoryPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="journal" element={<JournalPage />} />
        <Route path="learn" element={<LearnPage />} />
        <Route path="ideas" element={<TradeIdeasPage />} />
        <Route path="candidates" element={<CandidatesPage />} />
        <Route path="candidates/:runId" element={<CandidatesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/schwab" element={<SchwabSettingsPage />} />
        <Route path="settings/schwab/callback" element={<SchwabCallbackPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="settings/tools" element={<ToolsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
